/**
 * Centralised API error formatter.
 *
 * Turns whatever a rejected request/thunk hands us into a clear, user-facing
 * message, following this priority:
 *   1. Laravel validation errors  ({ errors: { field: [msg,...] } })  -> list every message
 *   2. A safe, human error string ({ error } / { message } / plain string)
 *   3. A generic fallback.
 *
 * Database / server internals are never shown. Anything that looks like a raw
 * DB or SQL error is replaced with the friendly fallback so the user only ever
 * sees things they can act on (e.g. "The name field is required").
 */

// Signatures of low-level errors that must never reach the user.
const DB_ERROR_SIGNS = [
  "sqlstate", "sql syntax", "integrity constraint", "foreign key",
  "pdoexception", "queryexception", "duplicate entry", "column not found",
  "unknown column", "no such table", "deadlock", "connection refused",
  "getaddrinfo", "econnrefused", "database", "->", "1064", "1452", "23000",
];

const looksLikeDbError = (s) => {
  const t = String(s).toLowerCase();
  return DB_ERROR_SIGNS.some((sign) => t.includes(sign));
};

// A Laravel validation bag is a plain object whose values are (arrays of) strings.
const isValidationBag = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const values = Object.values(obj);
  if (values.length === 0) return false;
  return values.every(
    (v) => Array.isArray(v) || typeof v === "string" || typeof v === "number"
  );
};

const flattenBag = (bag) =>
  Object.values(bag)
    .flat()
    .map((m) => String(m).trim())
    .filter(Boolean);

/**
 * @param {*} err       Axios error, thunk rejected payload, string, or object.
 * @param {string} fallback  Message shown when nothing user-friendly is found.
 * @returns {string} A clear, user-facing message (may contain newlines).
 */
export function getApiErrorMessage(err, fallback = "Something went wrong. Please review the form and try again.") {
  if (!err) return fallback;

  // Unwrap an axios error to its response body when present.
  const data = err && err.response && err.response.data !== undefined ? err.response.data : err;

  // 1) Validation errors — either { errors: {...} } or the bag itself.
  const bag = data && data.errors ? data.errors : (isValidationBag(data) ? data : null);
  if (bag) {
    const msgs = flattenBag(bag).filter((m) => !looksLikeDbError(m));
    if (msgs.length) return msgs.join("\n");
  }

  // 2) A single friendly message. NOTE: we intentionally never read `details`
  //    (that is where the backend puts raw DB/exception text).
  let single = null;
  if (typeof data === "string") single = data;
  else if (data && typeof data.error === "string") single = data.error;
  else if (data && typeof data.message === "string") single = data.message;

  if (single) {
    return looksLikeDbError(single) ? fallback : single;
  }

  return fallback;
}

export default getApiErrorMessage;
